import { MessageFilled, MessageOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { FC, useCallback, useState } from "react";
import { TaskAssignmentPayload, TaskStatus } from "tm-shared/types";

import { CommentSelectModal, StatusCycleButton } from "@/domains/tasks";
import { StatusIndicator } from "@/domains/tasks";
import { useAppDispatch } from "@/hooks/redux";
import { BottomSheet } from "@/shared/ui/BottomSheet";
import { OutlineIconButton } from "@/shared/ui/OutlineIconButton";
import { PrimaryText } from "@/shared/ui/Typography";
import { patchAssignmentsEntity } from "@/store/slices/progress";

import { StyledBottomSheetTop } from "../../Styled";
import { StyledBooleanAssignment, StyledCommentIndicator } from "./Styled";

interface BoolProps {
    taskId: string;
    payload?: TaskAssignmentPayload;
    onChangeStatus: (status: TaskStatus) => void;
}

export const Bool: FC<BoolProps> = ({ payload, taskId, onChangeStatus }) => {
    const [modal, setModal] = useState<"comment" | null>(null);
    const [open, setOpen] = useState(false);
    const hasComment = !!payload?.comment;
    const status = payload?.status ?? TaskStatus.Incomplete;
    const dispatch = useAppDispatch();

    const handleChangeStatus = useCallback(
        (status: TaskStatus) => {
            onChangeStatus(status);
        },
        [onChangeStatus],
    );

    const onChangeComment = useCallback(
        (comment: string) => {
            dispatch(
                patchAssignmentsEntity({
                    taskId,
                    patch: { comment },
                }),
            );
        },
        [dispatch, taskId],
    );

    const handleOpenBottomSheet = useCallback(() => {
        setOpen(true);
    }, []);

    const handleCloseBottomSheet = useCallback(() => {
        setOpen(false);
    }, []);

    return (
        <>
            <StyledBooleanAssignment onClick={handleOpenBottomSheet}>
                <PrimaryText>Підтвердження</PrimaryText>

                <Flex justify="center" align="center">
                    <StyledCommentIndicator>
                        {hasComment && (
                            <StatusIndicator type="warning">
                                <MessageFilled />
                            </StatusIndicator>
                        )}
                    </StyledCommentIndicator>

                    <StatusCycleButton
                        value={status}
                        onChange={handleChangeStatus}
                        dimension={48}
                        border={false}
                    />
                </Flex>
            </StyledBooleanAssignment>

            <Flex
                style={{
                    width: "100%",
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                }}
            >
                <BottomSheet open={open} onClose={handleCloseBottomSheet}>
                    <StyledBottomSheetTop>
                        <PrimaryText>Підтвердження</PrimaryText>
                        <Flex gap="20px" justify="center" align="center">
                            <OutlineIconButton
                                icon={<MessageOutlined />}
                                onClick={() => setModal("comment")}
                                disabled={
                                    !payload?.status ||
                                    payload?.status === TaskStatus.Incomplete
                                }
                            />
                            <StatusCycleButton
                                value={payload?.status ?? TaskStatus.Incomplete}
                                onChange={onChangeStatus}
                            />
                        </Flex>
                    </StyledBottomSheetTop>
                </BottomSheet>
            </Flex>

            {modal === "comment" && (
                <CommentSelectModal
                    onCloseModal={() => setModal(null)}
                    onConfirm={onChangeComment}
                    initialComment={payload?.comment ?? ""}
                />
            )}
        </>
    );
};
